import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

const FAQ = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // FAQ Categories with questions and answers
  const faqCategories = [
    {
      category: "General Questions",
      faqs: [
        {
          question: "What is MyEventShop?",
          answer: "MyEventShop is an all-in-one event platform that helps creators manage, promote, and monetize their events while building community. We combine ticketing, marketing tools, and attendee engagement in one seamless platform."
        },
        {
          question: "Is MyEventShop free to use?",
          answer: "MyEventShop offers both free and paid options. You can create and publish events for free, with a small fee applied only when tickets are sold. We also offer premium plans for professional event creators with additional features."
        },
        {
          question: "How do I contact customer support?",
          answer: "You can reach our customer support team through our Contact page, by emailing support@myeventshop.com, or by using the live chat feature available during business hours."
        },
        {
          question: "Is MyEventShop available worldwide?",
          answer: "Yes, MyEventShop is available globally. We support multiple currencies and payment methods for international events and attendees."
        }
      ]
    },
    {
      category: "For Event Creators",
      faqs: [
        {
          question: "How do I create an event on MyEventShop?",
          answer: "Creating an event is simple! After signing up, click on 'Create Event' in your dashboard. Follow the step-by-step process to add event details, ticketing options, and promotional materials. Once published, your event will be live and ready for attendees."
        },
        {
          question: "What kind of events can I host on MyEventShop?",
          answer: "MyEventShop supports a wide range of events, including workshops, classes, conferences, meetups, performances, exhibitions, and more. Both online and in-person events are fully supported."
        },
        {
          question: "How do I receive payments from ticket sales?",
          answer: "After connecting your preferred payment account (bank, PayPal, etc.), payments are processed automatically. Funds are typically available for withdrawal 3-5 business days after your event concludes, depending on your payment provider."
        },
        {
          question: "What marketing tools are available on MyEventShop?",
          answer: "MyEventShop offers email campaign tools, social media integration, promotional code generation, affiliate marketing options, and analytics to track your marketing effectiveness."
        }
      ]
    },
    {
      category: "For Attendees",
      faqs: [
        {
          question: "How do I purchase tickets on MyEventShop?",
          answer: "Browse events on our homepage or use the search feature to find specific events. Select the event you're interested in, choose your ticket type, and proceed to checkout. You can pay using credit/debit card, PayPal, or other supported payment methods."
        },
        {
          question: "Can I get a refund if I can't attend an event?",
          answer: "Refund policies are set by event organizers and vary by event. You can find the specific refund policy on each event page. If eligible, you can request refunds through your 'My Tickets' section."
        },
        {
          question: "How do I access my tickets after purchase?",
          answer: "Your tickets are automatically sent to your email and are also available in your 'My Tickets' section on MyEventShop. You can print tickets or use the mobile QR code for check-in."
        },
        {
          question: "Can I transfer my ticket to someone else?",
          answer: "If the event organizer allows ticket transfers, you can reassign your ticket by going to 'My Tickets' and selecting the transfer option. You'll need to provide the new attendee's name and email."
        }
      ]
    },
    {
      category: "Technical Support",
      faqs: [
        {
          question: "My payment isn't going through. What should I do?",
          answer: "First, verify your payment information is correct. If problems persist, try a different payment method or browser. If you're still having issues, contact our support team with the error message and we'll help resolve the issue."
        },
        {
          question: "How do I reset my password?",
          answer: "Click 'Forgot Password' on the login page. Enter your email address and we'll send you a password reset link. Follow the instructions in the email to create a new password."
        },
        {
          question: "Is my personal information secure on MyEventShop?",
          answer: "Yes, we take data security seriously. MyEventShop is fully GDPR-compliant and uses industry-standard encryption to protect all personal and payment information. We never share your data with third parties without consent."
        },
        {
          question: "Can I use MyEventShop on my mobile device?",
          answer: "Yes, MyEventShop is fully responsive and works on all devices. We also have mobile apps for iOS and Android coming soon for an even better on-the-go experience."
        }
      ]
    }
  ];

  return (
    <Box>
      <AppMenu 
        onLoginClick={() => setIsLoginOpen(true)} 
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 5, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Frequently Asked Questions
          </Typography>
          <Typography 
            variant="h6"
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto', 
              mb: 4,
              color: 'text.secondary'
            }}
          >
            Find answers to the most common questions about MyEventShop
          </Typography>
        </Box>

        {/* FAQ Sections */}
        <Box sx={{ mb: 8 }}>
          {faqCategories.map((category, categoryIndex) => (
            <Paper
              key={`category-${categoryIndex}`}
              elevation={2}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                mb: 5,
                overflow: 'hidden',
              }}
            >
              <Typography 
                variant="h5" 
                component="h2" 
                color="primary" 
                fontWeight="bold"
                sx={{ mb: 3 }}
              >
                {category.category}
              </Typography>
              
              <Divider sx={{ mb: 3 }} />
              
              {category.faqs.map((faq, faqIndex) => {
                const panelId = `panel-${categoryIndex}-${faqIndex}`;
                return (
                  <Accordion 
                    key={panelId}
                    expanded={expanded === panelId}
                    onChange={handleChange(panelId)}
                    sx={{ 
                      mb: 2,
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '8px !important',
                      overflow: 'hidden',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`${panelId}-content`}
                      id={`${panelId}-header`}
                      sx={{ 
                        bgcolor: 'rgba(95, 60, 111, 0.03)',
                        '&:hover': { bgcolor: 'rgba(95, 60, 111, 0.06)' } 
                      }}
                    >
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="medium"
                        sx={{ fontSize: '1.05rem' }}
                      >
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ py: 2 }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Paper>
          ))}
        </Box>
        
        {/* Still Have Questions Section */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 8,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" component="h2" mb={2} fontWeight="bold">
            Still Have Questions?
          </Typography>
          <Typography variant="body1" paragraph>
            Can't find the answer you're looking for? Please visit our Help Center
            for more detailed information or contact our support team directly.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Email us at: <strong>support@myeventshop.com</strong>
          </Typography>
        </Paper>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default FAQ; 